import { IPageProps } from '@ucheba/utils/helpers/components/types'
import {
  IPropButtonText,
  IPropDescription,
  IPropHref,
  IPropList,
  IPropName,
  IPropTitle,
} from '@ucheba/utils/helpers/components/prop.types'
import { IModColor } from '@ucheba/utils/helpers/styles/mod.types'
import { ISchoolDataContent } from './bll/useConvertSchoolData'

export interface ISchoolPageProps extends IPageProps<ISchoolDataContent> {
  data: ISchoolPage | null
}

export interface ISchoolPageDefaultData {
  type?: ESchoolPageTypes
  subject?: string
  classValue?: ESchoolPageClassValues
}

export interface ISchoolPage {
  seo: ISchoolPageSeo[]
  hero: ISchoolPageHero[]
  indicators: ISchoolPageIndicators
  about: ISchoolPageAbout[]
  aboutPlatform: ISchoolPageAboutPlatform
  offer: ISchoolPageOffer[]
  tariffs: ISchoolPageTariffs[]
  teachers: IAboutTeachers
  feedbacks: ISchoolPageFeedbacks
  questions: ISchoolPageQuestions[]
}

export interface ISchoolPageAboutPlatform {
  aboutVideoLessons: IAboutVideoLessons
  aboutHomeworks: IAboutHomeworks
  aboutWebinarsAndTests: IAboutWebinarsAndTests
  aboutDashboard: IAboutDashboard
  aboutOrganization: IAboutOrganization
}

interface IAboutVideoLessons {
  text: string
  list: IImgUrlAndTextAndVideoUrl[]
}

interface IAboutHomeworks {
  text: string
  list: {
    desktop: IImgUrlAndTextAndVideoUrl[]
    touch: IImgUrlAndTextAndVideoUrl[]
  }
}
interface IAboutWebinarsAndTests {
  webinars: ITitleAndTextAndImrUrl
  tests: ITitleAndTextAndImrUrl
}

interface IAboutDashboard {
  text: string
  images: string[]
}

interface IImgUrlAndTextAndVideoUrl {
  imgUrl: string
  text: string
  videoUrl?: string
}

interface ITitleAndTextAndImrUrl {
  text: string
  imgUrl: string
}

interface IAboutOrganization {
  buttonText: string
  list: IImgUrlAndTextAndVideoUrl[]
}

export interface ISchoolPageIndicators {
  graduates: {
    count: string
    text: string
    color: string
  }
  middleEGE: {
    count: string
    text: string
    color: string
  }
  middleOGE: {
    count: string
    text: string
    color: string
  }
  age: {
    count: string
    text: string
    color: string
  }
}

export interface ISchoolPageQuestions extends ISchoolPageDefaultData {
  title?: string
  list?: {
    href: string
    title: string
    buttonText: string
    description: string[]
  }[]
}

export interface ISchoolPageFeedbacks {
  title: string
  list: {
    slugs: string[] | null
    count?: string | null
    cardTitle?: string
    description?: string
    role?: string
    href: string
    overlay: string
    socials: {
      href: string
      name: string
      type: string
    }
    overlayImag: string
    previewImage: string
  }[]
}

export interface ISchoolPageTariffs extends ISchoolPageDefaultData {
  title?: {
    desktop: string
    touch: string
  }
  products?: ISchoolTariffVariant[]
}

export interface ISchoolPageOffer extends ISchoolPageDefaultData {
  title?: {
    desktop: string
    touch: string
  }
}

export interface ISchoolPageAbout extends ISchoolPageDefaultData {
  title?: string
  studying?: {
    title: {
      desktop: string
      touch: string
    }
    text: string
  }
  feedback?: {
    title: {
      desktop: string
      touch: string
    }
    text: string
  }
  aboutPlatform?: {
    title: {
      desktop: string
      touch: string
    }
    text: string
  }
  curator?: {
    title: {
      desktop: string
      touch: string
    }
    text: string
  }
  aboutProgram?: {
    title: {
      desktop: string
      touch: string
    }
    text: string
  }
  result?: {
    title: {
      desktop: string
      touch: string
    }
    text: string
  }
}

export interface ISchoolPageHero extends ISchoolPageDefaultData {
  title?: {
    touch: string
    desktop: string
  }
  description?: {
    touch: string
    desktop: string
  }
  stickImg?: string | null
  requestFormTitle?: string
  imgMessagesSrc?: {
    touch: string
    desktop: string
  }
  teacherImg?: {
    touch: string
    desktop: string
  }
  chipsText?: string
  category: EEgeSubjectCategory
}

export enum EEgeSubjectCategory {
  formal = 'formal',
  humonistic = 'humonistic',
  natural = 'natural',
  languages = 'languages',
}

export interface ISchoolPageSeo extends ISchoolPageDefaultData {
  title?: string
  description?: string
  image?: string
  jsonLd?: string
}

export interface ISchoolTariffVariant {
  text: string
  disable: boolean
  help: string
}

export interface IAboutTeachers
  extends IPropTitle,
    IPropButtonText,
    Required<IPropHref>,
    Required<IPropList<IAboutTeachersListItem>> {
  teachersList: ITeacher[]
  name?: string
  subjectId?: string | number
}

export interface ITeacher extends IVideo<ITeacherVideoOverlay> {
  personal: ITeacherPersonal
}

interface IVideo<OverlayType> extends Required<IPropHref>, IVideoOverlay<OverlayType> {
  previewImage?: string
}

interface ITeacherVideoOverlay {
  avgScore: {
    text: string
    scoreText: string
  }

  experience: {
    text: string
    scoreText: string
  }
}

interface IVideoOverlay<T> {
  overlay?: T
}

interface ITeacherPersonalSubjectItem extends IPropName {
  iconUrl: string
}

interface ITeacherPersonal
  extends IPropName,
    Required<IPropDescription>,
    Required<IPropList<IAboutTeachersListItem>> {
  subjects: ITeacherPersonalSubjectItem[]
  previewImageSqure?: string | null
}

export interface IAboutTeachersListItem extends IModColor {
  text: string
  iconName: string
}

export enum ESchoolPageTypes {
  default = 'default',
  ege = 'ege',
  oge = 'oge',
}

export enum ESchoolPageClassValues {
  class_9 = '9',
  class_10 = '10',
  class_11 = '11',
  default = 'default',
}
